import { useCallback, useMemo, useState } from 'react';
import { useCouponsStatisticsQuery } from '../../../hooks/useStatistics';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';

const defaultFilters = {
  possessor: 'all',
  orderBy: 'importe_mora_desc',
  minOverdueCoupons: '',
  maxOverdueCoupons: '',
  paymentMethod: '',
  contractNumber: '',
  debtStatus: '',
};

const defaultPagination = {
  page: 1,
  rowsPerPage: 10,
  total: 0,
  pages: 0,
};

export const useDebtStatusTable = () => {
  const { data: paymentMethods = [], isLoading: paymentMethodsLoading } = usePaymentMethods();

  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [pagination, setPagination] = useState(defaultPagination);

  const queryFilters = useMemo(() => {
    const payload = {
      reportType: 'debt-status-report',
      isPossessor: appliedFilters.possessor,
      orderBy: appliedFilters.orderBy,
      page: pagination.page,
      per_page: pagination.rowsPerPage,
      perPage: pagination.rowsPerPage,
    };

    if (appliedFilters.minOverdueCoupons !== '') {
      payload.minOverdueCoupons = Number(appliedFilters.minOverdueCoupons);
    }
    if (appliedFilters.maxOverdueCoupons !== '') {
      payload.maxOverdueCoupons = Number(appliedFilters.maxOverdueCoupons);
    }
    if (appliedFilters.paymentMethod) {
      payload.paymentMethod = appliedFilters.paymentMethod;
    }
    if (appliedFilters.contractNumber) {
      payload.contractNumber = appliedFilters.contractNumber.trim();
    }
    if (appliedFilters.debtStatus) {
      payload.debtStatus = appliedFilters.debtStatus;
    }

    return payload;
  }, [appliedFilters, pagination.page, pagination.rowsPerPage]);

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useCouponsStatisticsQuery(queryFilters);

  const rows = Array.isArray(response?.results?.table) ? response.results.table : [];
  const header = response?.header || null;
  const resultPagination = response?.results?.pagination || {};
  const loading = isLoading;
  const isRefetching = isFetching && Boolean(response);
  const error = isError ? 'No se pudo cargar el estado de mora.' : null;
  const effectivePage = resultPagination.page ?? pagination.page;
  const effectiveRowsPerPage =
    resultPagination.per_page ?? resultPagination.perPage ?? pagination.rowsPerPage;
  const total = resultPagination.total ?? 0;
  const pages = resultPagination.pages ?? 0;

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = useCallback((eventOrPage, pageValue) => {
    const newPage =
      typeof pageValue === 'number'
        ? pageValue
        : typeof eventOrPage === 'number'
          ? eventOrPage
          : pagination.page;

    setPagination((prev) => ({ ...prev, page: newPage }));
  }, [pagination.page]);

  const handleRowsPerPageChange = useCallback((eventOrValue) => {
    const newRowsPerPage =
      eventOrValue && typeof eventOrValue === 'object' && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue;
    const parsed = Number(newRowsPerPage);
    const safeRowsPerPage =
      Number.isFinite(parsed) && parsed > 0 ? parsed : pagination.rowsPerPage;
    setPagination((prev) => ({
      ...prev,
      page: 1,
      rowsPerPage: safeRowsPerPage,
    }));
  }, [pagination.rowsPerPage]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  }, [filters]);

  return {
    loading,
    isRefetching,
    error,
    rows,
    header,
    filters,
    pagination: {
      page: effectivePage,
      rowsPerPage: effectiveRowsPerPage,
      total,
      pages,
    },
    paymentMethods,
    paymentMethodsLoading,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleApplyFilters,
    reload: refetch,
  };
};
